import { useEffect, useState } from "react";
import { useSession } from "../../hooks/useSession";
import { supabase } from "../../lib/supabaseClient";
import "./Profile.css";

export default function ProfileSettings() {
    const { session, loading: sessionLoading } = useSession();
    const [uploading, setUploading] = useState(false);

    const [form, setForm] = useState({
        username: "",
        display_name: "",
        bio: "",
        avatar_url: "",
        is_public: false,
    });

    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    const getExt = (fileName = "") => {
        const parts = fileName.split(".");
        return parts.length > 1 ? parts.pop().toLowerCase() : "png";
    };

    const isAllowedImage = (file) => {
        const okTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"];
        return okTypes.includes(file.type);
    };

    const onAvatarUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // allow re-selecting same file
        setMsg("");

        if (!file) return;
        if (!session?.user) {
            setMsg("You must be logged in.");
            return;
        }
        if (!isAllowedImage(file)) {
            setMsg("Please upload a PNG, JPG, WEBP, or GIF.");
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setMsg("Max file size is 2MB.");
            return;
        }

        try {
            setUploading(true);

            const uid = session.user.id;
            const ext = getExt(file.name);
            const fileName = `avatar.${ext}`; // always overwrite the same name
            const path = `${uid}/${fileName}`;

            // upload (overwrite)
            const { error: uploadError } = await supabase.storage
                .from("avatars")
                .upload(path, file, {
                    upsert: true,
                    cacheControl: "3600",
                    contentType: file.type,
                });

            if (uploadError) throw uploadError;

            // get public URL
            const { data: pub } = supabase.storage
                .from("avatars")
                .getPublicUrl(path);

            // cache-bust so browser shows latest after overwrite
            const publicUrl = `${pub.publicUrl}?v=${Date.now()}`;

            // save to profiles
            const { error: updateError } = await supabase
                .from("profiles")
                .update({ avatar_url: publicUrl })
                .eq("user_id", uid);

            if (updateError) throw updateError;

            setForm((prev) => ({ ...prev, avatar_url: publicUrl }));
            setMsg("Avatar updated!");
        } catch (err) {
            setMsg(err?.message || "Upload failed.");
        } finally {
            setUploading(false);
        }
    };

    useEffect(() => {
        const load = async () => {
            setMsg("");

            if (!session?.user) return;

            setLoading(true);

            const { data, error } = await supabase
                .from("profiles")
                .select("username, display_name, bio, avatar_url, is_public")
                .eq("user_id", session.user.id)
                .maybeSingle();

            setLoading(false);

            if (error) {
                setMsg(error.message);
                return;
            }

            if (data) {
                setForm({
                    username: data.username || "",
                    display_name: data.display_name || "",
                    bio: data.bio || "",
                    avatar_url: data.avatar_url || "",
                    is_public: data.is_public === true,
                });
            }
        };

        load();
    }, [session]);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const onSave = async (e) => {
        e.preventDefault();
        setMsg("");

        if (!session?.user) {
            setMsg("You must be logged in.");
            return;
        }

        setLoading(true);

        const payload = {
            display_name: form.display_name.trim() || null,
            bio: form.bio.trim() || null,
            avatar_url: form.avatar_url.trim() || null,
            is_public: form.is_public,
        };

        const { error } = await supabase
            .from("profiles")
            .update(payload)
            .eq("user_id", session.user.id);

        setLoading(false);

        if (error) {
            setMsg(error.message);
            return;
        }

        setMsg("Saved!");
    };

    if (sessionLoading) return null;

    if (!session?.user) {
        return (
            <div className="profile-container">
                <div className="profile-card">
                    <h2>My Profile</h2>
                    <p className="profile-subtext">
                        Log in to edit your profile.
                    </p>
                    <a className="profile-primary" href="/login">
                        Log in
                    </a>
                </div>
            </div>
        );
    }

    const publicUrl = form.username ? `/u/${form.username}` : null;

    return (
        <div className="profile-container">
            <div className="profile-card">
                <h2>My Profile</h2>
                <p className="profile-subtext">
                    Control what other players can see. If your profile is
                    private, only you can view it.
                </p>

                {publicUrl && (
                    <div className="profile-preview">
                        Your public link: <a href={publicUrl}>{publicUrl}</a>
                    </div>
                )}

                <form onSubmit={onSave}>
                    <div className="profile-field">
                        <label>Username (read-only)</label>
                        <input value={form.username} disabled />
                    </div>

                    <div className="profile-field">
                        <label>Display name</label>
                        <input
                            name="display_name"
                            value={form.display_name}
                            onChange={onChange}
                            placeholder="e.g., Wookrook"
                        />
                    </div>

                    <div className="profile-field">
                        <label>Bio</label>
                        <textarea
                            name="bio"
                            rows={4}
                            value={form.bio}
                            onChange={onChange}
                            placeholder="Tell people what you love doing in Palia…"
                        />
                    </div>

                    <div className="profile-field">
                        <label>Avatar</label>

                        {form.avatar_url ? (
                            <div className="profile-avatar-wrap">
                                <img
                                    className="profile-avatar"
                                    src={form.avatar_url}
                                    alt="avatar"
                                />
                            </div>
                        ) : null}

                        <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/gif"
                            onChange={onAvatarUpload}
                            disabled={uploading || loading}
                        />

                        <div
                            className="profile-subtext"
                            style={{ marginTop: 8 }}
                        >
                            PNG/JPG/WEBP/GIF • Max 2MB
                        </div>
                    </div>

                    <div className="profile-toggle">
                        <input
                            id="is_public"
                            type="checkbox"
                            name="is_public"
                            checked={form.is_public}
                            onChange={onChange}
                        />
                        <label htmlFor="is_public">
                            Make my profile public
                        </label>
                    </div>

                    <div className="profile-actions">
                        <button
                            className="profile-primary"
                            type="submit"
                            disabled={loading}
                        >
                            {loading ? "Saving..." : "Save"}
                        </button>
                    </div>

                    {msg && <div className="profile-message">{msg}</div>}
                </form>
            </div>
        </div>
    );
}
